import BvSkeleton from 'volta/components/bv-skeleton';
import BvTabsTabComponent from 'volta/components/bv-tabs/tab';
import classNames from 'volta/helpers/class-names';

import type { TOC } from '@ember/component/template-only';
import type { WithBoundArgs } from '@glint/template';

type TabBoundedArgs = WithBoundArgs<
  typeof BvTabsTabComponent,
  'onClick' | 'focusFn' | 'selectedTab' | 'expand'
>;

interface BvTabsSignature {
  Args: {
    condensed?: boolean;
    expand?: boolean;
    isLoading?: boolean;
    onSelect?: (id?: string) => void;
    selectedTab?: string;
  };
  Blocks: {
    default: [TabBoundedArgs];
    secondary: [TabBoundedArgs];
  };
  Element: HTMLDivElement;
}

const BvTabsComponent: TOC<BvTabsSignature> = <template>
  <div
    class={{classNames
      'bv-tabs'
      (if @expand 'bv-tabs--expand')
      (if @condensed 'bv-tabs--condensed')
    }}
    ...attributes
  >
    {{#if @isLoading}}
      <div class='bv-tabs__items u-mx-2 u-my-1' role='tablist'>
        <BvSkeleton @variation='tab' />
        <BvSkeleton @variation='tab' @delay='200ms' />
        <BvSkeleton @variation='tab' @delay='400ms' />
      </div>
    {{else}}
      {{#let
        (component
          BvTabsTabComponent
          onClick=@onSelect
          focusFn=@onSelect
          selectedTab=@selectedTab
          expand=@expand
        )
        as |TabComponent|
      }}
        {{#if (has-block)}}
          <div class='bv-tabs__items' role='tablist'>
            {{yield TabComponent}}
          </div>
        {{/if}}
        {{#if (has-block 'secondary')}}
          <div class='bv-tabs__secondary u-d-flex u-align-items-center u-mx-2'>
            {{yield TabComponent to='secondary'}}
          </div>
        {{/if}}
      {{/let}}
    {{/if}}
  </div>
</template>;

export default BvTabsComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvTabs: typeof BvTabsComponent;
    'bv-tabs': typeof BvTabsComponent;
  }
}
